/**
 * TenantDocuments — Tenant-facing document list.
 *
 * Shows all documents assigned to this tenant's tenant_link.
 * Tenants can:
 *   - View / download their documents
 *   - See signature status
 *   - Navigate directly to /sign/:token when a document awaits their signature
 */

import { useEffect, useState } from 'react';
import { FileText, ExternalLink, PenLine, Loader2, Check, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../context/useAppContext';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/formatters';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface TenantDocument {
    id: string;
    name: string;
    type: string;
    file_url: string | null;
    status: 'draft' | 'sent' | 'signed';
    signing_token: string | null;
    signed_at: string | null;
    created_at: string;
}

// ─── Status chip ───────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
    const cfg: Record<string, { label: string; color: string; bg: string }> = {
        draft: { label: 'Pending', color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
        sent: { label: 'Signature Required', color: '#d97706', bg: 'rgba(245,158,11,0.1)' },
        signed: { label: 'Signed', color: '#16a34a', bg: 'rgba(34,197,94,0.1)' },
    };
    const c = cfg[status] ?? cfg.draft;
    return (
        <span style={{
            padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700,
            color: c.color, background: c.bg,
            display: 'inline-flex', alignItems: 'center', gap: '5px',
        }}>
            {status === 'signed' && <Check size={11} />}
            {status === 'sent' && <Clock size={11} />}
            {c.label}
        </span>
    );
}

const DOC_TYPE_LABELS: Record<string, string> = {
    lease_agreement: 'Lease Agreement',
    handover_protocol: 'Handover Protocol',
    notice: 'Notice',
    custom: 'Document',
};

// ─── Main Component ────────────────────────────────────────────────────────────

export default function TenantDocuments() {
    const { user } = useAppContext();
    const [documents, setDocuments] = useState<TenantDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [noLink, setNoLink] = useState(false);

    useEffect(() => {
        const fetchDocs = async () => {
            if (!user.id && !user.email) return;
            try {
                setLoading(true);

                // 1. Find the tenant_link for this user
                let query = supabase
                    .from('tenant_links')
                    .select('id');

                if (user.id) {
                    query = query.eq('tenant_user_id', user.id);
                } else {
                    query = query.eq('tenant_email', user.email);
                }

                const { data: linkData } = await query.maybeSingle();

                if (!linkData) {
                    setNoLink(true);
                    setLoading(false);
                    return;
                }

                // 2. Fetch documents assigned to this tenant_link
                const { data: docs, error } = await supabase
                    .from('documents')
                    .select('id, name, type, file_url, status, signing_token, signed_at, created_at')
                    .eq('tenant_link_id', linkData.id)
                    .order('created_at', { ascending: false });

                if (error) throw error;
                setDocuments(docs || []);
            } catch (err) {
                console.error('[TenantDocuments] fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDocs();
    }, [user.id, user.email]);

    // ─── Loading ──────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <Loader2 className="animate-spin" size={40} color="var(--color-primary)" />
            </div>
        );
    }

    // ─── Not linked to any property ───────────────────────────────────────

    if (noLink) {
        return (
            <div className="page" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <div style={{
                    width: 64, height: 64, borderRadius: '50%', background: 'var(--color-bg-secondary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
                    border: '1px solid var(--color-border)',
                }}>
                    <FileText size={28} color="var(--color-text-tertiary)" />
                </div>
                <h2 style={{ margin: '0 0 0.75rem', color: 'var(--color-text)' }}>No property linked yet</h2>
                <p style={{ color: 'var(--color-text-secondary)', maxWidth: 380, marginInline: 'auto', lineHeight: 1.6 }}>
                    Your account is not yet linked to a rental property. Contact your landlord to receive documents.
                </p>
            </div>
        );
    }

    const pendingSignature = documents.filter((d) => d.status === 'sent');

    // ─── Render ───────────────────────────────────────────────────────────

    return (
        <div className="page">
            <div className="page__header" style={{ marginBottom: '2rem' }}>
                <div>
                    <h1 className="page__title">Documents</h1>
                    <p className="page__subtitle">Your lease agreements, notices and signed documents</p>
                </div>
            </div>

            {/* Signature-required alert banner */}
            {pendingSignature.length > 0 && (
                <div style={{
                    marginBottom: '1.5rem', padding: '1rem 1.5rem',
                    background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)',
                    borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px',
                }}>
                    <AlertCircle size={20} color="#d97706" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '2px' }}>Action required</div>
                        <div style={{ fontSize: '0.875rem', color: '#b45309' }}>
                            {pendingSignature.length} document{pendingSignature.length > 1 ? 's' : ''} require{pendingSignature.length === 1 ? 's' : ''} your signature.
                        </div>
                    </div>
                    {pendingSignature[0].signing_token && (
                        <Link
                            to={`/sign/${pendingSignature[0].signing_token}`}
                            style={{
                                borderRadius: '8px', padding: '0.5rem 1rem',
                                background: '#d97706', color: '#fff', fontWeight: 700,
                                textDecoration: 'none', fontSize: '0.875rem', flexShrink: 0,
                                display: 'flex', alignItems: 'center', gap: '5px',
                            }}
                        >
                            <PenLine size={14} /> Sign now
                        </Link>
                    )}
                </div>
            )}

            {/* Empty state */}
            {documents.length === 0 ? (
                <div className="data-panel" style={{ padding: '5rem 2rem', textAlign: 'center' }}>
                    <div style={{
                        width: 60, height: 60, borderRadius: '50%',
                        background: 'var(--color-bg-secondary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
                        border: '1px solid var(--color-border)',
                    }}>
                        <FileText size={26} color="var(--color-text-tertiary)" />
                    </div>
                    <h3 style={{ margin: '0 0 0.5rem', color: 'var(--color-text)' }}>No documents yet</h3>
                    <p style={{ margin: 0, color: 'var(--color-text-secondary)', maxWidth: 360, marginInline: 'auto', lineHeight: 1.6 }}>
                        Your landlord will share lease agreements and documents here. Check back soon.
                    </p>
                </div>
            ) : (
                <div className="data-panel">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Document</th>
                                <th>Type</th>
                                <th>Date Added</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documents.map((doc) => (
                                <tr key={doc.id}>
                                    {/* Name */}
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600 }}>
                                            <div style={{
                                                width: 32, height: 32, borderRadius: '8px',
                                                background: 'rgba(99,102,241,0.08)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                <FileText size={15} color="#6366f1" />
                                            </div>
                                            <div>
                                                <div>{doc.name}</div>
                                                {doc.signed_at && (
                                                    <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>
                                                        Signed {formatDate(doc.signed_at)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Type */}
                                    <td style={{ color: 'var(--color-text-secondary)' }}>
                                        {DOC_TYPE_LABELS[doc.type] ?? doc.type}
                                    </td>

                                    {/* Date */}
                                    <td style={{ color: 'var(--color-text-secondary)' }}>
                                        {formatDate(doc.created_at)}
                                    </td>

                                    {/* Status */}
                                    <td>
                                        <StatusChip status={doc.status} />
                                    </td>

                                    {/* Actions */}
                                    <td>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                            {doc.file_url && (
                                                <a
                                                    href={doc.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-secondary btn-sm"
                                                    title="View document"
                                                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0.4rem 0.75rem' }}
                                                >
                                                    <ExternalLink size={13} /> View
                                                </a>
                                            )}

                                            {doc.status === 'sent' && doc.signing_token && (
                                                <Link
                                                    to={`/sign/${doc.signing_token}`}
                                                    className="btn btn-primary btn-sm"
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px',
                                                        padding: '0.4rem 0.875rem',
                                                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                                        border: 'none', fontWeight: 700, textDecoration: 'none',
                                                        borderRadius: '8px',
                                                    }}
                                                >
                                                    <PenLine size={13} /> Sign Now
                                                </Link>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
